/**
 * Web performance metrics reporter.
 *
 * Buffers metrics and flushes them to the Hono BFF via sendBeacon,
 * following the same pattern as client-logger.ts. The BFF ingests
 * them as structured logs with { source: 'browser-metrics' }.
 *
 * The generated `client-metrics_gen.ts` calls `reportMetric` with a
 * typed metric name, numeric value, and label bag.
 */
import { create, toJsonString } from '@bufbuild/protobuf';
import { TELEMETRY_MAX_BATCH_EVENTS } from '@letsgethome/lib/telemetry';
import { BrowserMetricBatchSchema } from '@letsgethome/proto/telemetry/v1/telemetry_pb';

export interface MetricLabels {
  [label: string]: string;
}

// In-memory buffer item. Mirrors the proto `telemetry.v1.BrowserMetricEvent`
// field set; the buffer is converted to that message (via `create` +
// `toJsonString`) at flush, so the wire is proto3-canonical JSON.
interface MetricEvent {
  name: string;
  value: number;
  labels?: MetricLabels;
  ts: number;
  url: string;
}

const FLUSH_INTERVAL_MS = 10_000;
// Flush threshold = the ingest endpoint's per-request cap, ONE shared
// number (Rust `domain_core::telemetry` SSOT) — see server/api/telemetry.ts.
const MAX_BUFFER_SIZE = TELEMETRY_MAX_BATCH_EVENTS;
const METRICS_ENDPOINT = '/api/telemetry/metrics';

let buffer: MetricEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

function flush(): void {
  if (buffer.length === 0) return;

  const batch = create(BrowserMetricBatchSchema, {
    metrics: buffer.map((m) => ({
      name: m.name,
      value: m.value,
      ...(m.labels !== undefined ? { labels: m.labels } : {}),
      ts: BigInt(m.ts),
      url: m.url,
    })),
  });
  buffer = [];

  const payload = toJsonString(BrowserMetricBatchSchema, batch);
  const blob = new Blob([payload], { type: 'application/json' });
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(METRICS_ENDPOINT, blob);
  } else {
    fetch(METRICS_ENDPOINT, {
      method: 'POST',
      body: blob,
      keepalive: true,
    }).catch(() => {
      /* best-effort */
    });
  }
}

function ensureFlush(): void {
  if (flushTimer !== null || typeof window === 'undefined') return;

  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', () => flush());
}

export function reportMetric(name: string, value: number, labels?: MetricLabels): void {
  if (typeof window === 'undefined') return;

  ensureFlush();
  buffer.push({
    name,
    value,
    labels,
    ts: Date.now(),
    url: location.pathname,
  });

  if (buffer.length >= MAX_BUFFER_SIZE) flush();
}
